// React & React Hooks
import { useEffect, useState, useContext } from "react";

// Third-party libraries
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Typography, Button, Card, Stack } from "@mui/material";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Services / utilities
import { httpPost, encryptData } from "../../services/commonService";
import { endApiLoader, startApiLoader } from "../../services/apiLoaderService";

// Custom components
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import { selectUserDetail } from "../../redux/slice/userSlice";

const RippleSecureSignup = () => {
  const { t } = useTranslation();
  const X_API_KEY = process.env.REACT_APP_RIPPLE_X_API_KEY;
  const rippleSecureSignInURL = process.env.REACT_APP_RIPPLESECURE_SIGNIN_URL;
  const userDetail = useSelector(selectUserDetail);
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [isAlreadyExistWithRippleSecure, setIsAlreadyExistWithRippleSecure] =
    useState(null);
  const [signupAttempted, setSignupAttempted] = useState(false);

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      setIsAlreadyExistWithRippleSecure(
        userDetail.UserAccountDetails?.IsAlreadyExistWithRippleSecure
      );
    }
  }, [userDetail?.UserAccountDetails?.AcctID]);

  const createRippleAccount = async () => {
    if (!userDetail) return;
    startApiLoader();
    try {
      const modal = {
        RegistrationThrough: "ThirdParty",
        RefID: 0,
        FirstName: userDetail.UserAccountDetails?.PvtFirstName,
        LastName: userDetail.UserAccountDetails?.PvtLastName,
        Email: userDetail.UserAccountDetails?.PvtEmail,
        Password: encryptData("Admin@123"),
        ThirdPartyAcctID: userDetail.UserAccountDetails?.AcctID,
        ThirdPartySponserEmail:
          userDetail.UserAccountDetails?.ReferenceEmailForRippleSecureSignup,
      };

      const options = {
        method: "post",
        headers: {
          "content-type": "application/json; charset=UTF-8",
          impersonateEmail: impersonateEmail,
          "x-api-key": X_API_KEY,
        },
        body: JSON.stringify(modal),
      };

      const response = await fetch(
        urlConstant.RippleSecureSignUp.CreateRippleAccount,
        options
      );

      let rippleData;
      if (
        response.status === 200 ||
        response.status === 400 ||
        response.status === 401 ||
        response.status === 404
      ) {
        rippleData = await response.json();
      } else {
        endApiLoader();
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      setSignupAttempted(true);

      if (
        rippleData?.Status === dataConstant.APIResponseStatus.Success ||
        (rippleData?.Status === dataConstant.APIResponseStatus.Failure &&
          rippleData?.error === "User already exists with same email!")
      ) {
        const dataModel = {
          AcctID: userDetail.UserAccountDetails.AcctID,
          RippleAcctID: rippleData?.Values?.AcctID,
        };

        httpPost(
          urlConstant.RippleSecureSignUp.AddRippleAcctIdInDB,
          dataModel,
          true,
          false,
          accessToken,
          impersonateEmail
        )
          .then((response) => {
            if (
              response?.Value?.Status === dataConstant.APIResponseStatus.Success
            ) {
              showSnackbar(response.Value.Message, "success");
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            } else {
              showSnackbar(response?.Value?.Message, "error");
            }
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => endApiLoader());
      } else if (
        rippleData?.Status === dataConstant.APIResponseStatus.Failure
      ) {
        endApiLoader();
        showSnackbar(rippleData?.error, "error");
      } else {
        endApiLoader();
        showSnackbar(rippleData?.error, "error");
      }
    } catch (err) {
      endApiLoader();
      console.error(err);
    }
  };

  return (
    <Box flexDirection="column" gap={2} display="flex">
      <Typography color="secondary.light" variant="h4" fontWeight={600}>
        {t("RippleSignUp.Title")}
      </Typography>

      <Box width={"100%"}>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            border: "1px solid var(--headerbg)",
            borderRadius: "20px",
            height: "100%",
          })}
        >
          {userDetail && (
            <Box display="flex" justifyContent="center" width="100%">
              <Card
                sx={(theme) => ({
                  p: {
                    xs: "20px",
                    sm: "25px 30px",
                  },
                  background: theme.palette.common.black,
                  border: "1px solid var(--headerbg)",
                  borderRadius: "20px",
                  height: "100%",
                  width: "100%",
                })}
              >
                <Stack gap="20px">
                  {isAlreadyExistWithRippleSecure ? (
                    <Stack
                      direction="column"
                      spacing={2}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={600}
                        color="secondary.light"
                        textAlign={"center"}
                      >
                        {t("RippleSignUp.YouAreAlreadyUserTitle")}
                      </Typography>
                      <Box>
                        <Typography
                          variant="body2"
                          color="secondary.text"
                          className="line-height-2"
                        >
                          {t("RippleSignUp.YouAreAlreadyUserSubTitle")}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          window.open(rippleSecureSignInURL, "_blank")
                        }
                        sx={{
                          mt: 2,
                          px: 4,
                          py: 1.5,
                          borderRadius: "10px",
                          fontSize: "1rem",
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        {t("RippleSignUp.Signin")}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack
                      direction="column"
                      spacing={2}
                      alignItems="center"
                      sx={{ width: "100%" }}
                      textAlign={"center"}
                    >
                      <Typography
                        variant="h4"
                        color="secondary.light"
                        fontWeight={600}
                      >
                        {t("RippleSignUp.AddUserTitle")}
                      </Typography>
                      <Box sx={{ maxWidth: "90%", width: "100%" }}>
                        <Typography
                          variant="body2"
                          color="secondary.text"
                          className="line-height-2"
                        >
                          {t("RippleSignUp.AddUserSubTitle")}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={createRippleAccount}
                        sx={{
                          mt: 2,
                          px: 4,
                          py: 1.5,
                          borderRadius: "10px",
                          fontSize: "1rem",
                          width: { xs: "100%", sm: "auto" },
                        }}
                        disabled={signupAttempted}
                      >
                        {t("RippleSignUp.Signup")}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Box>
          )}
        </Card>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </Box>
  );
};

export default RippleSecureSignup;
